import React, { Suspense, useMemo, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Await, useParams, useRouteLoaderData } from 'react-router-dom'

import LoadingSpinner from '@components/shared/LoadingSpinner'
import NFIcon from '@components/shared/NFIcon'
import { Chapter } from '@pages/home/loaders/home.loader'
import { usePageTitle } from '@providers/LayoutProvider'
import { useSession } from '@stores/globalData/globalData.store'
import PageRoutes from '@utils/page-routes'

import type { HomeLoaderResponse, RedeemedContent } from '@pages/home/loaders/home.loader'

const RedeemedContentPage: React.FC = () => {
  const { t } = useTranslation()
  const data = useRouteLoaderData('homeRoot') as HomeLoaderResponse
  const session = useSession()
  const params = useParams()

  usePageTitle(t('titles.book'))

  const shouldShow = useMemo(
    () => session.gatedFeatures.showGatedFeature('bookDemoUsers'),
    [session]
  )

  if (!shouldShow) {
    return null
  }
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Await resolve={data.redeemedContent}>
        {({ redemptions }) => {
          const redemption = redemptions.find((r) => r.id === params.id) as RedeemedContent | null
          if (!redemption) return null

          return (
            <div className="table w-full my-5 overflow-hidden relative bookStyle1">
              <div
                className="w-full overflow-hidden bg-white dark:bg-black-bg2"
                dangerouslySetInnerHTML={{ __html: redemption.bookBeginHTML }}
              />
              <div>
                {redemption.chapters.map((chapter) => (
                  <Chapter chapter={chapter} key={chapter.title} />
                ))}
              </div>
              <div
                className="w-full overflow-hidden bg-white dark:bg-black-bg2"
                dangerouslySetInnerHTML={{ __html: redemption.bookEndHTML }}
              />
            </div>
          )
        }}
      </Await>
    </Suspense>
  )
}

export default RedeemedContentPage

interface ChapterProps {
  chapter: Chapter
}
const Chapter: React.FC<ChapterProps> = ({ chapter }) => {
  const [expanded, setExpanded] = useState(false)
  return (
    <div key={chapter.title} className="border-b border-b-gray-border border-dotted bg-white">
      <button
        type="button"
        className="bg-white hover:bg-gray-light px-5 py-2.5 w-full text-left"
        onClick={() => setExpanded(!expanded)}
        dangerouslySetInnerHTML={{ __html: chapter.title }}></button>
      <div
        className={classNames(
          'mx-5 overflow-hidden transition-all duration-500',
          expanded ? 'max-h-[1000px]' : 'max-h-0'
        )}>
        {chapter.sections.map((section) => (
          <div key={JSON.stringify(section)}>
            {section.type === 'html' && section.html && (
              <div className="p-2.5" dangerouslySetInnerHTML={{ __html: section.html }}></div>
            )}
            {section.type === 'scoreLink' && section.externalId && (
              <div className="mb-2.5 flex flex-row w-full h-[40px] items-center hover:bg-gray-light">
                <NFIcon icon="note" className="text-xl mx-2" />
                <a
                  href={PageRoutes.editorPath(section.externalId)}
                  className="h-full flex flex-row items-center flex-1">
                  {section.title && (
                    <span
                      className="ellipsify"
                      dangerouslySetInnerHTML={{ __html: section.title }}></span>
                  )}
                </a>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
